import { useState, useEffect, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LoaderCircle } from "lucide-react";
import { toast } from "react-toastify";
import { useLangPrefix } from "../hooks/useLangPrefix";
import LanguageSwitcher from "../components/layout/LanguageSwitcher";
import PasswordInput from "../components/common/PasswordInput";
import { useAuthStore } from "../store/authStore";
import * as Sentry from "@sentry/react";

export default function LoginPage() {
  const { t } = useTranslation();
  const { prefix } = useLangPrefix();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const login = useAuthStore((s) => s.login);
  const loginWithGoogle = useAuthStore((s) => s.loginWithGoogle);
  const token = useAuthStore((s) => s.token);
  const hasHydrated = useAuthStore((s) => s._hasHydrated);
  const branchBootstrapStatus = useAuthStore((s) => s.branchBootstrapStatus);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const busy = loading || googleLoading;
  const callbackUrl = searchParams.get("callback_url") || "";
  const safeCallbackUrl = useMemo(() => {
    if (!callbackUrl) return "";
    if (callbackUrl.startsWith("/") && !callbackUrl.startsWith("//")) {
      return callbackUrl;
    }
    return "";
  }, [callbackUrl]);
  const nextPath = safeCallbackUrl || `${prefix}/dashboard`;
  const signupHref = safeCallbackUrl
    ? `${prefix}/signup?callback_url=${encodeURIComponent(safeCallbackUrl)}`
    : `${prefix}/signup`;

  const isBranchBootstrapSettled =
    branchBootstrapStatus === "ready" ||
    branchBootstrapStatus === "no_branch" ||
    branchBootstrapStatus === "no_org" ||
    branchBootstrapStatus === "error";

  useEffect(() => {
    if (!hasHydrated || !token || !isBranchBootstrapSettled) return;
    navigate(nextPath, { replace: true });
  }, [hasHydrated, token, isBranchBootstrapSettled, navigate, nextPath]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    Sentry.addBreadcrumb({ message: "Login attempt", level: "info", data: { email } });
    const result = await login(email, password);
    setLoading(false);
    if (result.ok) {
      return;
    } else {
      toast.error(result.error);
    }
  };

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    Sentry.addBreadcrumb({ message: "Google sign-in attempt", level: "info" });
    const result = await loginWithGoogle();
    setGoogleLoading(false);
    if (result.ok) {
      return;
    } else {
      toast.error(result.error);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-zinc-100 p-4 text-zinc-900 scheme-light">
      <div className="absolute right-4 top-4 z-10 flex items-center gap-2">
        <LanguageSwitcher />
      </div>
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-indigo-100/70 via-transparent to-cyan-100/50" />

      <div className="w-full max-w-sm relative">
        <div className="text-center mb-8">
          <div className="inline-flex items-center">
            <img
              src="/full_logo.png"
              alt="Growchat"
              className="w-40 h-auto rounded-md object-cover"
            />
          </div>
          <p className="text-sm text-gray-500">{t("login.tagline")}</p>
        </div>

        <div className="card p-6">
          <h2 className="mb-5 text-lg font-semibold text-zinc-900">
            {t("login.title")}
          </h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="mb-1.5 block text-xs font-medium text-zinc-600">
                {t("login.email")}
              </label>
              <input
                type="text"
                inputMode="email"
                autoComplete="username"
                className="input"
                placeholder={t("login.emailPlaceholder")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div>
              <div className="flex justify-between items-center gap-2 mb-1.5">
                <label className="block text-xs font-medium text-zinc-600">
                  {t("login.password")}
                </label>
                <Link
                  to={`${prefix}/forgot-password`}
                  className="shrink-0 text-xs font-medium text-indigo-600 hover:text-indigo-700"
                >
                  {t("login.forgotPassword")}
                </Link>
              </div>
              <PasswordInput
                className="input"
                placeholder={t("login.passwordPlaceholder")}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <button
              type="submit"
              disabled={busy}
              className="btn-primary w-full justify-center py-2.5 mt-1 disabled:opacity-50"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <Spinner /> {t("login.signingIn")}
                </span>
              ) : (
                t("login.signIn")
              )}
            </button>
          </form>

          <div className="relative mt-5 mb-5">
            <div className="absolute inset-0 flex items-center" aria-hidden>
              <div className="w-full border-t border-zinc-200" />
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-white px-3 text-gray-500">
                {t("login.dividerOr")}
              </span>
            </div>
          </div>

          <button
            type="button"
            onClick={handleGoogleSignIn}
            disabled={busy}
            className="flex w-full items-center justify-center gap-3 rounded-full border border-zinc-300 bg-white px-4 py-2.5 text-sm font-medium text-zinc-900 transition-colors hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {googleLoading ? (
              <span className="flex items-center gap-2">
                <Spinner /> {t("login.signingInWithGoogle")}
              </span>
            ) : (
              <>
                <GoogleMark className="shrink-0" />
                {t("login.signInWithGoogle")}
              </>
            )}
          </button>

          <p className="text-center text-xs text-gray-600 mt-5">
            {t("login.demoHint")}
          </p>
          <p className="text-center text-xs text-gray-500 mt-3">
            {t("login.noAccount")}{" "}
            <Link
              to={signupHref}
              className="font-medium text-indigo-600 hover:text-indigo-700"
            >
              {t("login.signUp")}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

function Spinner() {
  return <LoaderCircle className="h-4 w-4 animate-spin" />;
}

function GoogleMark({ className = "" }) {
  return (
    <svg className={`h-5 w-5 ${className}`} viewBox="0 0 24 24" aria-hidden>
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
}
